import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, X, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Compact banner shown at the top of the Admin panel when there are
 * unresolved email send failures. Lets the admin see which sends failed
 * (Resend domain issues, sandbox restrictions, quota, etc.) and dismiss
 * each item once it has been addressed.
 */
export default function EmailFailuresBanner() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/email-failures");
      setItems(data.items || []);
      setCount(data.unresolved_count || 0);
    } catch (err) {
      // Silent: this banner should not disrupt the admin panel if it fails
      console.warn("email-failures load error", err?.response?.status);
    }
  };

  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/email-failures/${id}/dismiss`);
      toast.success("Registro descartado.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo descartar.");
    } finally { setBusy(false); }
  };

  if (count === 0) return null;

  return (
    <div
      data-testid="email-failures-banner"
      className="mb-6 rounded-xl border border-[var(--mep-pink)]/50 bg-[rgba(232,61,132,0.08)] overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[rgba(232,61,132,0.05)] transition"
        data-testid="email-failures-toggle"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle size={20} className="text-[var(--mep-pink)]" />
          <div>
            <div className="text-sm font-medium text-[var(--mep-pink)]">
              {count} {count === 1 ? "email no enviado" : "emails no enviados"}
            </div>
            <div className="text-xs text-[var(--mep-text-muted)] mt-0.5">
              Toca para ver los detalles y comprobar la configuración de Resend / dominio.
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp size={18} className="text-[var(--mep-text-muted)]" /> : <ChevronDown size={18} className="text-[var(--mep-text-muted)]" />}
      </button>

      {expanded && (
        <div className="border-t border-[var(--mep-pink)]/30 divide-y divide-[var(--mep-border)]" data-testid="email-failures-list">
          {items.map((r) => (
            <div key={r.id} className="px-5 py-4 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--mep-gold)] border border-[var(--mep-gold)]/40 rounded-full px-2 py-0.5">
                    {kindLabel(r.kind)}
                  </span>
                  <span className="text-sm text-[var(--mep-text)] font-medium">{r.to_email || "—"}</span>
                  <span className="text-xs text-[var(--mep-text-muted)]">{formatDate(r.created_at)}</span>
                </div>
                {r.sender && (
                  <div className="text-xs text-[var(--mep-text-muted)] mt-1">
                    Remitente usado: <span className="font-mono">{r.sender}</span>
                  </div>
                )}
                <div className="text-xs text-[var(--mep-pink)] mt-1 break-words">
                  {r.error_message}
                </div>
              </div>
              <button
                type="button"
                onClick={() => dismiss(r.id)}
                disabled={busy}
                className="flex-shrink-0 flex items-center gap-1 text-xs text-[var(--mep-text-muted)] hover:text-white border border-[var(--mep-border)] rounded-full px-3 py-1.5 transition"
                data-testid={`email-failure-dismiss-${r.id}`}
              >
                <X size={12} /> Descartar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const kindLabel = (k) => {
  switch ((k || "").toLowerCase()) {
    case "welcome": return "Bienvenida";
    case "password_reset": return "Recuperación";
    case "invitation": return "Invitación";
    default: return k || "—";
  }
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};
